'use client';

import { ChevronRight } from '@ps/web-fusion-icons';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { LinkProps as AriaLinkProps } from 'react-aria-components';
import { Link as AriaLink } from 'react-aria-components';

import Icon from '../Icon/Icon';
import { useFusionTheme } from '../hooks';

import styles from './Tile.module.scss';

export type Props = Omit<AriaLinkProps, 'children' | 'style'> &
  VariantProps<typeof tileVariants> & {
    icon: React.FC<React.SVGProps<SVGElement>>;
    title: string;
    body: string;
  };

const tileVariants = cva(styles.container, {
  variants: {
    size: {
      small: styles.sizeSmall,
      medium: false
    }
  }
});

const Tile = ({ size = 'medium', icon, className, title, body, ...linkProps }: Props) => {
  const { addThemeClassName } = useFusionTheme();

  if (size === 'small' && body) {
    throw new Error('Body text should be used with medium size Tile.');
  }

  return (
    <AriaLink className={addThemeClassName(tileVariants({ size }), className)} {...linkProps}>
      <div className={styles.header}>
        <Icon icon={icon} aria-hidden />
        <div className={styles.title}>{title}</div>
        {size === 'small' && <Icon icon={ChevronRight} aria-hidden />}
      </div>
      {size === 'medium' && <div className={styles.body}>{body}</div>}
    </AriaLink>
  );
};

export default Tile;
